import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useCart } from '@/context/CartContext';
import { ShoppingCart, User, LogOut, LayoutDashboard, Menu, X, Package, Wrench, Image as ImageIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';

const Navbar = () => {
  const { user, logout } = useAuth();
  const { cart } = useCart();
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    setIsMenuOpen(false);
    navigate('/');
  };

  const menuItems = [
    { label: 'Products', path: '/products', icon: Package },
    { label: 'Services', path: '/services', icon: Wrench },
    { label: 'Gallery', path: '/gallery', icon: ImageIcon },
  ];

  return (
    <nav className={`sticky top-0 z-[100] transition-all duration-300 ${isMenuOpen ? 'bg-white' : 'bg-white/80 backdrop-blur-xl'} border-b border-slate-200`} data-testid="main-navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          <Link to="/" className="flex items-center" data-testid="logo-link">
            <img 
              src="/logo.png" 
              alt="Digital Power" 
              className="h-[4.5rem] w-auto object-contain drop-shadow-sm"
              data-testid="navbar-logo"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {menuItems.map((item) => (
              <Link 
                key={item.label}
                to={item.path} 
                className="text-slate-700 hover:text-primary font-medium transition-colors"
              >
                {item.label}
              </Link>
            ))}
          </div>

          <div className="flex items-center gap-2 md:gap-4">
            <Link to="/cart" className="relative p-2 hover:bg-slate-50 rounded-full transition-colors" data-testid="nav-cart">
              <ShoppingCart className="h-6 w-6 text-slate-700 hover:text-primary transition-colors" />
              {cart.length > 0 && (
                <span className="absolute top-0 right-0 bg-secondary text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-bold" data-testid="cart-count">
                  {cart.length}
                </span>
              )}
            </Link>
            
            <div className="hidden md:flex items-center gap-2">
              {user ? (
                <>
                  <Link to="/dashboard" data-testid="nav-dashboard">
                    <Button variant="outline" className="flex items-center gap-2 border-primary text-primary hover:bg-primary/5 hover:text-primary transition-all">
                      <LayoutDashboard className="h-4 w-4" />
                      <span>Dashboard</span>
                    </Button>
                  </Link>
                  <Button variant="ghost" onClick={handleLogout} data-testid="logout-button" className="p-2">
                    <LogOut className="h-4 w-4" />
                  </Button>
                </>
              ) : (
                <Link to="/auth" data-testid="nav-login">
                  <Button className="bg-primary hover:bg-primary/90 text-white font-bold uppercase tracking-wider px-6">
                    Login
                  </Button>
                </Link>
              )}
            </div>

            {/* Mobile Menu Toggle */}
            <button 
              className="lg:hidden p-2 text-slate-700 hover:text-primary transition-colors"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? <X className="h-7 w-7" /> : <Menu className="h-7 w-7" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {isMenuOpen && (
        <div className="lg:hidden fixed inset-x-0 bottom-0 top-[5rem] bg-[#ffffff] z-[99] animate-in fade-in slide-in-from-top-2 duration-300 ease-in-out shadow-2xl">
          <div className="flex flex-col h-full bg-white p-8 gap-6 overflow-y-auto">
            <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">Navigation</p>
            {menuItems.map((item, index) => (
              <Link
                key={item.label}
                to={item.path}
                className={`flex items-center justify-between p-4 rounded-xl border border-slate-50 hover:bg-primary/5 hover:border-primary/40 transition-all duration-200 animate-in fade-in slide-in-from-left-4 fill-mode-both`}
                style={{ animationDelay: `${index * 50}ms` }}
                onClick={() => setIsMenuOpen(false)}
              >
                <div className="flex items-center gap-4">
                  <div className="p-2 bg-slate-50 rounded-lg group-hover:bg-white transition-colors">
                    <item.icon className="h-5 w-5 text-primary" />
                  </div>
                  <span className="text-lg font-semibold text-slate-800">{item.label}</span>
                </div>
                <div className="h-2 w-2 rounded-full bg-primary/20"></div>
              </Link>
            ))}
            
            <div className="flex flex-col gap-4 pt-8 mt-auto">
              <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">Account</p>
              {user ? (
                <>
                  <Link 
                    to="/dashboard" 
                    className="flex items-center gap-4 p-4 rounded-xl bg-primary/5 text-slate-800 font-semibold border border-primary/20 hover:border-primary/40 transition-colors"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <div className="p-2 bg-white rounded-lg shadow-sm">
                      <LayoutDashboard className="h-5 w-5 text-primary" />
                    </div>
                    Dashboard
                  </Link>
                  <Button 
                    variant="outline" 
                    className="w-full text-lg py-7 border-red-100 text-red-600 font-bold bg-red-50/50 hover:bg-red-50 hover:border-red-200 transition-all rounded-xl"
                    onClick={handleLogout}
                  >
                    <LogOut className="h-5 w-5 mr-3" />
                    LOGOUT
                  </Button>
                </>
              ) : (
                <Link to="/auth" onClick={() => setIsMenuOpen(false)}>
                  <Button className="w-full text-lg py-7 bg-primary font-bold shadow-lg shadow-primary/30 rounded-xl">
                    LOGIN / REGISTER
                  </Button>
                </Link>
              )}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
